/**
 * Production-ready logging system for IntentDrive.ai
 * Replaces console.debug statements with structured logging
 */

export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3
}

export interface LogEntry {
  timestamp: string;
  level: LogLevel;
  category: string;
  message: string;
  data?: any;
  sessionId?: string;
  userId?: string;
}

class Logger {
  private logLevel: LogLevel;
  private sessionId?: string;
  private userId?: string;
  private logBuffer: LogEntry[] = [];
  private readonly maxBufferSize = 100;

  constructor() {
    // Set log level based on environment
    this.logLevel = process.env.NODE_ENV === 'development' ? LogLevel.DEBUG : LogLevel.WARN;
  }

  setSession(sessionId: string) {
    this.sessionId = sessionId;
  }

  setUser(userId: string) {
    this.userId = userId;
  }

  private createLogEntry(level: LogLevel, category: string, message: string, data?: any): LogEntry {
    return {
      timestamp: new Date().toISOString(),
      level,
      category,
      message,
      data,
      sessionId: this.sessionId,
      userId: this.userId
    };
  }

  private shouldLog(level: LogLevel): boolean {
    return level >= this.logLevel;
  }

  private addToBuffer(entry: LogEntry) {
    this.logBuffer.push(entry);
    
    // Keep buffer size manageable
    if (this.logBuffer.length > this.maxBufferSize) {
      this.logBuffer.shift();
    }
  }

  private formatForConsole(entry: LogEntry): string {
    const prefix = `[${entry.category}]`;
    const dataStr = entry.data ? ` ${JSON.stringify(entry.data)}` : '';
    return `${prefix} ${entry.message}${dataStr}`;
  }

  debug(category: string, message: string, data?: any) {
    if (!this.shouldLog(LogLevel.DEBUG)) return;

    const entry = this.createLogEntry(LogLevel.DEBUG, category, message, data);
    this.addToBuffer(entry);
    
    if (process.env.NODE_ENV === 'development') {
      console.debug(this.formatForConsole(entry));
    }
  }

  info(category: string, message: string, data?: any) {
    if (!this.shouldLog(LogLevel.INFO)) return;

    const entry = this.createLogEntry(LogLevel.INFO, category, message, data);
    this.addToBuffer(entry);
    
    console.info(this.formatForConsole(entry));
  }

  warn(category: string, message: string, data?: any) {
    if (!this.shouldLog(LogLevel.WARN)) return;

    const entry = this.createLogEntry(LogLevel.WARN, category, message, data);
    this.addToBuffer(entry);
    
    console.warn(this.formatForConsole(entry));
  }

  error(category: string, message: string, data?: any) {
    if (!this.shouldLog(LogLevel.ERROR)) return;

    const entry = this.createLogEntry(LogLevel.ERROR, category, message, data);
    this.addToBuffer(entry);
    
    console.error(this.formatForConsole(entry));
    
    // In production, you might want to send errors to a logging service
    if (process.env.NODE_ENV === 'production') {
      this.sendToLoggingService(entry);
    }
  }

  private async sendToLoggingService(entry: LogEntry) {
    // Implementation would depend on your logging service
    // Example: Sentry, LogRocket, DataDog, etc.
    try {
      // await loggingService.send(entry);
    } catch (error) {
      // Fallback to console if logging service fails
      console.error('Failed to send log to service:', error);
    }
  }

  // Get recent logs for debugging
  getRecentLogs(count: number = 50): LogEntry[] {
    return this.logBuffer.slice(-count);
  }

  // Clear log buffer
  clearLogs() {
    this.logBuffer = [];
  }

  // Export logs for debugging
  exportLogs(): string {
    return JSON.stringify(this.logBuffer, null, 2);
  }
}

// Create singleton instance
export const logger = new Logger();

// Convenience functions for common chat logging patterns
export const chatLogger = {
  conversation: (message: string, data?: any) => logger.debug('CONVERSATION', message, data),
  message: (message: string, data?: any) => logger.debug('MESSAGE', message, data),
  flow: (message: string, data?: any) => logger.debug('FLOW', message, data),
  ai: (message: string, data?: any) => logger.debug('AI', message, data),
  ui: (message: string, data?: any) => logger.debug('UI', message, data),
  error: (message: string, data?: any) => logger.error('CHAT', message, data),
  performance: (message: string, data?: any) => logger.info('PERFORMANCE', message, data)
};
